type LogEntry = {
  timestamp: string;
  type: "info" | "error";
  message: string;
  params: unknown;
};

// Queue for batching logs before saving to storage
let logQueue: LogEntry[] = [];
let isSaving = false;

// Handle saving logs in batches with debouncing
const processLogQueue = async (): Promise<void> => {
  if (isSaving || logQueue.length === 0) return;

  try {
    isSaving = true;
    const logsToSave = [...logQueue];
    logQueue = [];

    // Safely access chrome.storage API
    if (
      typeof chrome !== "undefined" &&
      chrome.storage &&
      chrome.storage.local
    ) {
      // Get existing logs
      const result = await chrome.storage.local.get(["extensionLogs"]);
      const existingLogs: LogEntry[] = result.extensionLogs || [];

      // Combine logs
      const allLogs = [...existingLogs, ...logsToSave];

      // Limit size to prevent performance issues
      const finalLogs = allLogs.length > 200 ? allLogs.slice(-100) : allLogs;

      // Save logs back to storage
      await chrome.storage.local.set({ extensionLogs: finalLogs });
    }
  } catch (error) {
    console.error("Error saving logs to storage:", error);
  } finally {
    isSaving = false;

    // Process any new logs that arrived during saving
    if (logQueue.length > 0) {
      setTimeout(processLogQueue, 1000);
    }
  }
};

// Add log to queue and trigger processing
const queueLog = (
  type: "info" | "error",
  message: string,
  params: unknown
): void => {
  const timestamp = new Date().toISOString();
  const newLog: LogEntry = { timestamp, type, message, params };
  logQueue.push(newLog);

  // Debounce processing to avoid too many storage operations
  if (!isSaving) {
    setTimeout(processLogQueue, 1000);
  }
};

// Safe console logging that works in all contexts
export const logInfo = (message: string, params?: unknown): void => {
  console.log(message, params ?? "");
  queueLog("info", message, params);
};

export const logError = (message: string, params?: unknown): void => {
  console.error(message, params ?? "");
  queueLog("error", message, params);
};

// Safely get logs from storage
const getLogsFromStorage = async (): Promise<LogEntry[]> => {
  if (
    typeof chrome === "undefined" ||
    !chrome.storage ||
    !chrome.storage.local
  ) {
    return [];
  }

  try {
    const result = await chrome.storage.local.get(["extensionLogs"]);
    return result.extensionLogs || [];
  } catch (error) {
    console.error("Error retrieving logs:", error);
    return [];
  }
};

// Export utility functions
export const printAllLogs = async (): Promise<void> => {
  try {
    // Process any pending logs first
    await processLogQueue();

    const logs = await getLogsFromStorage();

    if (logs.length === 0) {
      console.log("No logs found");
      return;
    }

    console.group("Extension Logs");
    console.log(`Total logs: ${logs.length}`);

    logs.forEach((log, index) => {
      const logMethod = log.type === "error" ? console.error : console.log;
      const formattedTime = new Date(log.timestamp).toLocaleString();

      console.group(
        `${index + 1}. [${formattedTime}] ${log.type.toUpperCase()}`
      );
      logMethod("Message:", log.message);
      logMethod("Params:", log.params);
      console.groupEnd();
    });

    console.groupEnd();
  } catch (error) {
    console.error("Error printing logs:", error);
  }
};

export const clearAllLogs = async (): Promise<void> => {
  try {
    if (
      typeof chrome !== "undefined" &&
      chrome.storage &&
      chrome.storage.local
    ) {
      await chrome.storage.local.set({ extensionLogs: [] });
      console.log("All logs have been cleared");
      // Clear queue too
      logQueue = [];
    }
  } catch (error) {
    console.error("Error clearing logs:", error);
  }
};

// Safely attach to window object if available
const attachToWindow = (): void => {
  if (typeof window === "undefined") return;

  try {
    // Define functions directly on window
    Object.defineProperties(window, {
      printAllLogs: {
        value: printAllLogs,
        writable: false,
        configurable: true,
      },
      clearAllLogs: {
        value: clearAllLogs,
        writable: false,
        configurable: true,
      },
      logInfo: {
        value: logInfo,
        writable: false,
        configurable: true,
      },
      logError: {
        value: logError,
        writable: false,
        configurable: true,
      },
    });

    console.log("Logger functions attached to window successfully");
  } catch (error) {
    console.error("Error attaching log functions to window:", error);
  }
};

// Attach functions when this module loads
attachToWindow();

// Add a function to manually attach to window (useful for content scripts)
export const setupGlobalLogger = attachToWindow;
